import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const MIN_COMMISSION_TO_KEEP = 1;

export const formatMainHunterCommission = (
  partyLicenseFees: Record<string, number> | undefined,
): string => {
  const totalShareCommission = Object.values(partyLicenseFees ?? {}).reduce(
    (sum, value) => sum + (value ?? 0),
    0,
  );

  if (totalShareCommission + MIN_COMMISSION_TO_KEEP > 100) {
    return `< ${MIN_COMMISSION_TO_KEEP}`;
  }

  const mainHunterCommission = 100 - totalShareCommission;

  return Math.min(
    100,
    Math.max(mainHunterCommission, MIN_COMMISSION_TO_KEEP),
  ).toFixed(2);
};

const VALIDATION_RESOURCES =
  Resources.Hunter.UnassignedMerchants.AssignDialog.Validation;

export const getAssignHunterFormSchema = (t: TFunction) =>
  z
    .object({
      primaryLicensePublicID: z
        .string()
        .min(1, t(VALIDATION_RESOURCES.PrimaryRequired)),
      partyLicenseFees: z.record(z.string(), z.number()),
    })
    .refine(
      ({ partyLicenseFees }) =>
        Object.values(partyLicenseFees).every((v) => v >= 0 && v < 100),
      {
        message: t(VALIDATION_RESOURCES.ShareOutOfRange),
        path: ["partyLicenseFees"],
      },
    )
    .refine(
      ({ partyLicenseFees }) =>
        Object.values(partyLicenseFees).reduce((sum, v) => sum + v, 0) +
          MIN_COMMISSION_TO_KEEP <=
        100,
      {
        message: t(VALIDATION_RESOURCES.PrimaryUnderMin),
        path: ["partyLicenseFees"],
      },
    );

export type AssignHunterFormData = z.infer<
  ReturnType<typeof getAssignHunterFormSchema>
>;
